import WebAPIUtils from 'js/WebAPIUtils';
import {
  Interval,
  isSatelliteLayer,
  MetaTypes,
  PrescriptionJob,
  PrescriptionJobPostBody,
  PrescriptionJobPutBody,
} from 'js/components/Prescription/PrescriptionJob';
import { VariableRateMap, VariableRateMapUpdate } from 'js/model/prescription/VariableRateMap';
import SourceTiff from 'js/model/prescription/SourceTiff';
import {
  calculateClassificationIntervalAreas,
  calculateIntervalAreas,
  calculateOverridesArea,
} from '../components/Prescription/PrescriptionUtils';
import { isDemandLayerType } from '../model/surveys/country/SurveyCountryStrategy';
import { Formats } from '../components/Prescription/Downloading/PrescriptionDownloader';
import { mapSeedingCalculatorInputsToApi } from './MapSeedingCalcInput';
import PrescriptionModelReference from '../model/prescription/PrescriptionModelReference';
import { trackSeedingPrescriptionCreated } from '../components/Analytics/trackSeedingPrescriptionCreated';

const GET_PRESCRIPTION_MAPS = 'fieldsense/PrescriptionReducer/GET_PRESCRIPTION_MAPS';
const GET_PRESCRIPTION_DOCUMENTATION =
  'fieldsense/PrescriptionReducer/GET_PRESCRIPTION_DOCUMENTATION';
const DELETE_PRESCRIPTION_DOCUMENTATION =
  'fieldsense/PrescriptionReducer/DELETE_PRESCRIPTION_DOCUMENTATION';
const SAVE_PRESCRIPTION_JOB_LOCALLY =
  'fieldsense/PrescriptionReducer/SAVE_PRESCRIPTION_JOB_LOCALLY';
const DELETE_PRESCRIPTION_JOBS = 'fieldsense/PrescriptionReducer/DELETE_PRESCRIPTION_JOBS';
const SET_PRESCRIPTION_JOB_SEASON_ID =
  'fieldsense/PrescriptionReducer/SET_PRESCRIPTION_JOB_SEASON_ID';
const SET_CURRENT_JOB = 'fieldsense/PrescriptionReducer/SET_CURRENT_JOB';
const RESET_CURRENT_JOB = 'fieldsense/PrescriptionReducer/RESET_CURRENT_JOB';
const SET_FUTURE_JOB = 'fieldsense/PrescriptionReducer/SET_FUTURE_JOB';
const RESET_FUTURE_JOB = 'fieldsense/PrescriptionReducer/RESET_FUTURE_JOB';
const UPDATE_FUTURE_JOB = 'fieldsense/PrescriptionReducer/UPDATE_FUTURE_JOB';
const SET_BULK_PRESCRIPTION_FIELDS = 'fieldsense/PrescriptionReducer/SET_BULK_PRESCRIPTION_FIELDS';
const SET_IS_BULK_PRESCRIPTION = 'fieldsense/PrescriptionReducer/SET_IS_BULK_PRESCRIPTION';
const SET_APPLIED_INITIAL_INTERVALS =
  'fieldsense/PrescriptionReducer/SET_APPLIED_INITIAL_INTERVALS';
const SET_BULK_PRESCRIPTION_JOB = 'fieldsense/PrescriptionReducer/SET_BULK_PRESCRIPTION_JOB';
const SET_BULK_PRESCRIPTION_JOBS = 'fieldsense/PrescriptionReducer/SET_BULK_PRESCRIPTION_JOBS';
const SET_BULK_PRESCRIPTION_PREFIX = 'fieldsense/PrescriptionReducer/SET_BULK_PRESCRIPTION_PREFIX';
const RESET_BULK_PRESCRIPTIONS = 'fieldsense/PrescriptionReducer/RESET_BULK_PRESCRIPTIONS';
const SET_BULK_PRESCRIPTION_FUTURE_JOB =
  'fieldsense/PrescriptionReducer/SET_BULK_PRESCRIPTION_FUTURE_JOB';
const SET_BULK_PRESCRIPTION_FUTURE_JOBS =
  'fieldsense/PrescriptionReducer/SET_BULK_PRESCRIPTION_FUTURE_JOBS';
const RESET_BULK_PRESCRIPTION_FUTURE_JOB =
  'fieldsense/PrescriptionReducer/RESET_BULK_PRESCRIPTION_FUTURE_JOB';
const SET_SEEDING_CALCULATOR_PARAMS =
  'fieldsense/PrescriptionReducer/SET_SEEDING_CALCULATOR_PARAMS';

function parseMetaType(metaType) {
  if (metaType === MetaTypes.SPOT_SPRAYING) {
    return MetaTypes.SPRAYING;
  } else {
    return metaType;
  }
}

export function downloadShapefilePromise(downloadJob, farm, survey, format) {
  const { jobId, jobName, layer, legacy } = downloadJob;

  if (isSatelliteLayer(layer)) {
    if (legacy) {
      return WebAPIUtils.downloadShapefileLegacy(farm.farmId, jobId, jobName);
    } else {
      return WebAPIUtils.downloadShapefile(jobId, jobName, format);
    }
  } else {
    return WebAPIUtils.generateShapefile(downloadJob, farm, survey, format);
  }
}

export function downloadISOXMLPromise(downloadJob, farm, format) {
  const { jobId, jobName, layer } = downloadJob;
  if (isSatelliteLayer(layer)) {
    if (format === Formats.KVERNELAND_ISOXML) {
      format = Formats.KVERNELAND_ISOXML.concat('_new');
    }
    return WebAPIUtils.downloadISOXML(jobId, jobName, format);
  } else {
    return WebAPIUtils.generateISOXMLfile(downloadJob, farm, format);
  }
}

// Bulk shapefile download. Bulk prescriptions always share a single source
// layer type across all jobs, so we branch once on `jobs[0].layer`. No legacy
// branch is required because bulk prescriptions are always newly created.
export function downloadShapefileBulkPromise(jobs, farm, format, jobName) {
  const layer = jobs[0].layer;
  if (isSatelliteLayer(layer)) {
    const vrmIds = jobs.map((j) => j.jobId);
    return WebAPIUtils.downloadShapefileBulk(vrmIds, jobName, format);
  } else {
    return WebAPIUtils.generateShapefileBulk(jobs, farm, jobName, format);
  }
}

// Bulk ISOXML download. Same single-layer-type assumption as the shapefile
// bulk path. Preserves the satellite `KVERNELAND_ISOXML -> kverneland_new`
// suffix mapping used by the single-job path.
export function downloadISOXMLBulkPromise(jobs, farm, format, jobName) {
  const layer = jobs[0].layer;
  if (isSatelliteLayer(layer)) {
    let fmt = format;
    if (fmt === Formats.KVERNELAND_ISOXML) {
      fmt = Formats.KVERNELAND_ISOXML.concat('_new');
    }
    const vrmIds = jobs.map((j) => j.jobId);
    return WebAPIUtils.downloadISOXMLBulk(vrmIds, jobName, fmt);
  } else {
    return WebAPIUtils.generateISOXMLBulk(jobs, farm, jobName, format);
  }
}

export const savePrescriptionJobPromise = (job: PrescriptionJob, farm): Promise => {
  return new Promise(async (resolve, reject) => {
    const payload = { ...job };

    const resolveSavedJob = (savedJob) => {
      if (!job.jobId && job.metaType === MetaTypes.SEEDING) {
        trackSeedingPrescriptionCreated(
          savedJob.jobId,
          Boolean(job.seedingCalculatorParams),
          job.prescriptionModel
        );
      }
      resolve(savedJob);
    };

    const seedingCalculatorParams = payload.seedingCalculatorParams
      ? mapSeedingCalculatorInputsToApi(payload.seedingCalculatorParams)
      : null;
    // Exclude the "key" property from the payload
    payload.intervals = payload.intervals.map((interval: Interval) => ({
      min: interval.min,
      max: interval.max,
      allocation: interval.prescription,
    }));

    if (isSatelliteLayer(job.layer)) {
      // Use the old API

      if (payload.jobId) {
        // Update existing job
        const body = new VariableRateMapUpdate(
          payload.jobName,
          parseMetaType(payload.metaType),
          payload.maxPrescription,
          payload.totalPrescription,
          payload.unit,
          payload.intervals,
          payload.overrides,
          payload.seasonId,
          payload.fieldId,
          [new SourceTiff(payload.date, 100)],
          seedingCalculatorParams
        );

        try {
          if (payload.usingJobPolygon) {
            await WebAPIUtils.putUpdateVariableRateMap(payload.jobId, body);
            resolveSavedJob({ ...job, saved: true });
          } else {
            await WebAPIUtils.postUpdateVariableRateMap(payload.jobId, body);
            resolveSavedJob({ ...job, saved: true });
          }
        } catch (error) {
          reject(error);
        }
      } else {
        // Save new job
        const body = new VariableRateMap(
          payload.jobName,
          parseMetaType(payload.metaType),
          payload.maxPrescription,
          payload.totalPrescription,
          payload.unit,
          'sentinel2',
          [new SourceTiff(payload.date, 100)],
          payload.intervals,
          payload.overrides,
          payload.seasonId,
          seedingCalculatorParams
        );

        WebAPIUtils.postVariableRateMap(payload.fieldId, body)
          .then((jobId) => {
            if (jobId !== '' && Number.isInteger(jobId)) {
              resolveSavedJob({ ...job, jobId, saved: true });
            } else {
              reject(new Error(`Couldn't parse job ID: ${jobId}`));
            }
          })
          .catch((e) => {
            reject(e);
          });
      }
    } else {
      // Use the new API

      if (payload.jobId) {
        const body = new PrescriptionJobPutBody();
        body.intervals = payload.intervals;
        body.layer = payload.layer.toUpperCase();
        body.maxPrescription = payload.maxPrescription;
        body.metaType = parseMetaType(payload.metaType);
        body.name = payload.jobName;
        body.overrides = payload.overrides;
        body.totalPrescription = payload.totalPrescription;
        body.unit = payload.unit;
        body.seasonId = payload.seasonId;
        body.layerType = payload.layerType;
        body.limeInfo = payload.limeInfo ? payload.limeInfo : null;
        body.vrmSet = payload.vrmSet ? payload.vrmSet : null;
        body.surveyImageId = payload.surveyImageId;
        body.seedingCalculatorInfo = seedingCalculatorParams;
        body.prescriptionModel = payload.prescriptionModel?.toApi();
        // legacy
        body.surveyId = payload.surveyId;

        try {
          await WebAPIUtils.putSurveyPrescriptionJob(payload.jobId, body, farm);
          resolveSavedJob({ ...job, saved: true });
        } catch (error) {
          reject(error);
        }
      } else {
        // Save new Prescription Job
        const body = new PrescriptionJobPostBody();
        body.fieldId = payload.fieldId;
        body.intervals = payload.intervals;
        body.layer = payload.layer.toUpperCase();
        body.maxPrescription = payload.maxPrescription;
        body.metaType = parseMetaType(payload.metaType);
        body.name = payload.jobName;
        body.overrides = payload.overrides;
        body.surveyImageId = payload.surveyImageId;
        body.totalPrescription = payload.totalPrescription;
        body.unit = payload.unit;
        body.seasonId = payload.seasonId;
        body.layerType = payload.layerType;
        body.limeInfo = payload.limeInfo ? payload.limeInfo : null;
        body.vrmSet = payload.vrmSet != null ? payload.vrmSet : null;
        body.seedingCalculatorInfo = seedingCalculatorParams;
        body.prescriptionModel = payload.prescriptionModel?.toApi();

        try {
          const jobId = await WebAPIUtils.postSurveyPrescriptionJob(body, farm);

          if (jobId !== '') {
            resolveSavedJob({ ...job, jobId, saved: true });
          } else {
            reject(new Error(`Couldn't parse job ID: ${jobId}`));
          }
        } catch (error) {
          reject(error);
        }
      }
    }
  });
};

export function deletePrescriptionMapsPromise(jobs: Array<PrescriptionJob>, farm) {
  return Promise.all(
    jobs.map((job) => {
      if (job.legacy) {
        return WebAPIUtils.deleteVariableRateMapLegacy(farm.farmId, job.jobId);
      } else if (isSatelliteLayer(job.layer)) {
        return WebAPIUtils.deleteVariableRateMap(job.jobId);
      } else {
        return WebAPIUtils.deleteSurveyPrescriptionJob(job.jobId, farm.farmId);
      }
    })
  );
}

export function getPrescriptionMaps(farm, fields) {
  return {
    type: GET_PRESCRIPTION_MAPS,
    payload: WebAPIUtils.getPrescriptionMaps(farm, fields),
    meta: {
      farm,
      fields,
    },
  };
}

export function getPrescriptionDocumentation(farmId) {
  return {
    type: GET_PRESCRIPTION_DOCUMENTATION,
    payload: WebAPIUtils.getPrescriptionDocumentation(farmId),
  };
}

export function deletePrescriptionDocumentation(id) {
  return {
    type: DELETE_PRESCRIPTION_DOCUMENTATION,
    payload: WebAPIUtils.deletePrescriptionDocumentation(id),
  };
}

export function savePrescriptionJobLocally(job: PrescriptionJob) {
  return {
    type: SAVE_PRESCRIPTION_JOB_LOCALLY,
    payload: job,
  };
}

export function deletePrescriptionJobs(jobs, farm) {
  return {
    type: DELETE_PRESCRIPTION_JOBS,
    payload: deletePrescriptionMapsPromise(jobs, farm),
    meta: {
      jobs: jobs,
    },
  };
}

export function setPrescriptionJobSeasonId(farm, job: PrescriptionJob, seasonId: number) {
  const clone = PrescriptionJob.cloneJob(job);
  clone.seasonId = seasonId;

  return {
    type: SET_PRESCRIPTION_JOB_SEASON_ID,
    payload: WebAPIUtils.putPrescriptionJobSeasonid(farm, job, seasonId),
    meta: {
      job: clone,
    },
  };
}

export function setCurrentJob(jobChanges, recalculate = false) {
  return {
    type: SET_CURRENT_JOB,
    payload: jobChanges,
    meta: {
      recalculate: recalculate,
    },
  };
}

export function resetCurrentJob() {
  return {
    type: RESET_CURRENT_JOB,
    payload: null,
  };
}

export function setFutureJob(job: PrescriptionJob) {
  return {
    type: SET_FUTURE_JOB,
    payload: job,
  };
}

export function updateFutureJob(changes) {
  return {
    type: UPDATE_FUTURE_JOB,
    payload: changes,
  };
}

export function resetFutureJob() {
  return {
    type: RESET_FUTURE_JOB,
    payload: null,
  };
}

export function setBulkPrescriptionFields(fields) {
  return {
    type: SET_BULK_PRESCRIPTION_FIELDS,
    payload: fields,
  };
}

export function setIsBulkPrescription(isBulk) {
  return {
    type: SET_IS_BULK_PRESCRIPTION,
    payload: isBulk,
  };
}

export function setAppliedInitialIntervals(applied) {
  return {
    type: SET_APPLIED_INITIAL_INTERVALS,
    payload: applied,
  };
}

export function setBulkPrescriptionJob(fieldId, job: PrescriptionJob) {
  return {
    type: SET_BULK_PRESCRIPTION_JOB,
    payload: job,
    meta: {
      fieldId,
    },
  };
}

export function setBulkPrescriptionJobs(jobs: Record<number, PrescriptionJob>) {
  return {
    type: SET_BULK_PRESCRIPTION_JOBS,
    payload: jobs,
  };
}

export function setBulkPrescriptionPrefix(prefix) {
  return {
    type: SET_BULK_PRESCRIPTION_PREFIX,
    payload: prefix,
  };
}

export function resetBulkPrescriptions() {
  return {
    type: RESET_BULK_PRESCRIPTIONS,
    payload: null,
  };
}

export function setBulkPrescriptionFutureJob(fieldId, job: PrescriptionJob) {
  return {
    type: SET_BULK_PRESCRIPTION_FUTURE_JOB,
    payload: job,
    meta: {
      fieldId,
    },
  };
}

export function setBulkPrescriptionFutureJobs(jobs: Record<number, PrescriptionJob>) {
  return {
    type: SET_BULK_PRESCRIPTION_FUTURE_JOBS,
    payload: jobs,
  };
}

export function resetBulkPrescriptionFutureJob(fieldId) {
  return {
    type: RESET_BULK_PRESCRIPTION_FUTURE_JOB,
    meta: {
      fieldId,
    },
  };
}

export function setSeedingCalculatorParams(seedingCalculatorParams) {
  return {
    type: SET_SEEDING_CALCULATOR_PARAMS,
    payload: seedingCalculatorParams,
  };
}

/* ============== //
||     REDUCER    ||
// ============== */

const initState = {
  maps: null,
  currentJob: null,
  documentation: null,
  isBulkPrescription: false,
  bulkPrescription: {
    fields: [],
    appliedInitialIntervals: null,
    currentJobs: {},
    futureJobs: {},
    prefix: '',
  },
};

export default function reducer(state = initState, action) {
  switch (action.type) {
    case GET_PRESCRIPTION_MAPS + '_FULFILLED': {
      state = { ...state, maps: action.payload };
      break;
    }

    case GET_PRESCRIPTION_DOCUMENTATION + '_FULFILLED': {
      state = { ...state, documentation: action.payload };
      break;
    }

    case SET_CURRENT_JOB: {
      let newJob: PrescriptionJob = { ...state.currentJob, ...action.payload };
      if (action.meta.recalculate) {
        if (state.currentJob?.overrides !== newJob.overrides) {
          newJob.overrideAreas = calculateOverridesArea(newJob);
        }

        if (isDemandLayerType(newJob.layerType)) {
          newJob = calculateClassificationIntervalAreas(newJob);
        } else {
          newJob = calculateIntervalAreas(newJob);
        }
      }

      state = { ...state, currentJob: newJob };
      break;
    }

    case RESET_CURRENT_JOB: {
      state = { ...state, currentJob: null };
      break;
    }

    case SAVE_PRESCRIPTION_JOB_LOCALLY: {
      let newMaps = state.maps ? [...state.maps] : [];
      const map = action.payload;

      // If we save an existing map, remove it and push the updated map to the list
      const existing = newMaps.find((m) => m.jobId === map.jobId);
      if (existing) {
        newMaps = newMaps.filter((m) => m.jobId !== map.jobId);
      }

      newMaps = [map, ...newMaps];

      state = { ...state, maps: newMaps };
      break;
    }

    case DELETE_PRESCRIPTION_JOBS + '_FULFILLED': {
      const deleted = action.meta.jobs;

      const newMaps = state.maps
        ? [...state.maps].filter((m) => !deleted.find((d) => m.jobId === d.jobId))
        : null;

      state = { ...state, maps: newMaps };
      break;
    }

    case SET_PRESCRIPTION_JOB_SEASON_ID + '_FULFILLED': {
      const changedJob = action.meta.job;
      state = {
        ...state,
        maps: [...state.maps.filter((m) => m.jobId !== changedJob.jobId), changedJob],
      };
      break;
    }

    case SET_FUTURE_JOB: {
      state = {
        ...state,
        futureJob: action.payload,
      };
      break;
    }

    case UPDATE_FUTURE_JOB: {
      const jobWithPayloadChanges: PrescriptionJob = { ...state.futureJob, ...action.payload };
      const withRecalculatedOverrideAreas: PrescriptionJob = {
        ...jobWithPayloadChanges,
        overrideAreas:
          state.futureJob.overrides !== jobWithPayloadChanges.overrides
            ? calculateOverridesArea(jobWithPayloadChanges)
            : jobWithPayloadChanges.overrideAreas,
      };

      const updatedJob: PrescriptionJob = calculateIntervalAreas(withRecalculatedOverrideAreas);

      state = { ...state, futureJob: updatedJob };

      break;
    }

    case RESET_FUTURE_JOB: {
      state = {
        ...state,
        futureJob: null,
      };
      break;
    }

    case SET_BULK_PRESCRIPTION_FIELDS: {
      state = {
        ...state,
        bulkPrescription: {
          ...state.bulkPrescription,
          fields: action.payload,
        },
      };
      break;
    }

    case SET_IS_BULK_PRESCRIPTION: {
      state = {
        ...state,
        isBulkPrescription: action.payload,
      };
      break;
    }

    case SET_APPLIED_INITIAL_INTERVALS: {
      state = {
        ...state,
        bulkPrescription: {
          ...state.bulkPrescription,
          appliedInitialIntervals: action.payload,
        },
      };
      break;
    }

    case SET_BULK_PRESCRIPTION_JOB: {
      const { fieldId } = action.meta;
      const job = action.payload;

      state = {
        ...state,
        bulkPrescription: {
          ...state.bulkPrescription,
          currentJobs: {
            ...state.bulkPrescription.currentJobs,
            [fieldId]: job,
          },
        },
      };
      break;
    }

    case SET_BULK_PRESCRIPTION_PREFIX: {
      state = {
        ...state,
        bulkPrescription: {
          ...state.bulkPrescription,
          prefix: action.payload,
        },
      };
      break;
    }

    case SET_BULK_PRESCRIPTION_JOBS: {
      state = {
        ...state,
        bulkPrescription: {
          ...state.bulkPrescription,
          currentJobs: {
            ...state.bulkPrescription.currentJobs,
            ...action.payload,
          },
        },
      };
      break;
    }

    case SET_BULK_PRESCRIPTION_FUTURE_JOB: {
      const { fieldId } = action.meta;
      const job = action.payload;

      state = {
        ...state,
        bulkPrescription: {
          ...state.bulkPrescription,
          futureJobs: {
            ...state.bulkPrescription.futureJobs,
            [fieldId]: job,
          },
        },
      };
      break;
    }

    case SET_SEEDING_CALCULATOR_PARAMS: {
      const seedingCalcParams = action.payload;

      state = {
        ...state,
        currentJob: {
          ...state.currentJob,
          seedingCalculatorParams: seedingCalcParams,
        },
      };
      break;
    }

    case SET_BULK_PRESCRIPTION_FUTURE_JOBS: {
      state = {
        ...state,
        bulkPrescription: {
          ...state.bulkPrescription,
          futureJobs: {
            ...state.bulkPrescription.futureJobs,
            ...action.payload,
          },
        },
      };
      break;
    }

    case RESET_BULK_PRESCRIPTION_FUTURE_JOB: {
      const { fieldId } = action.meta;
      const remainingFutureJobs = { ...state.bulkPrescription.futureJobs };
      delete remainingFutureJobs[fieldId];

      state = {
        ...state,
        bulkPrescription: {
          ...state.bulkPrescription,
          futureJobs: remainingFutureJobs,
        },
      };
      break;
    }

    case RESET_BULK_PRESCRIPTIONS: {
      state = {
        ...state,
        isBulkPrescription: false,
        bulkPrescription: {
          fields: [],
          appliedInitialIntervals: null,
          currentJobs: {},
          futureJobs: {},
          prefix: '',
        },
      };
      break;
    }

    default:
      break;
  }

  return state;
}
